import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const categories = [
  { label: "All", value: "all" },
  { label: "Account", value: "account" },
  { label: "Team Management", value: "team_management" },
  { label: "Basics", value: "basics" },
  { label: "Federal", value: "federal" },
  { label: "401k", value: "401k" },
  { label: "403b", value: "403b" },
];

const ARTICLES_PER_PAGE = 5; // Number of articles per page

const KnowledgeBaseContent: React.FC<{ articles: KnowledgeBaseArticle[] }> = ({
  articles,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter the articles based on search term and category
  const filteredArticles = articles.filter((article) => {
    const matchesSearch = article.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate pagination variables
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  // Handle pagination
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="flex items-center gap-4 mb-6">
        <input
          type="text"
          placeholder="Search articles..."
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-primary text-sm"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page on search/filter change
          }}
        />

        {/* Category Select */}
        <Select
          onValueChange={(value) => {
            setSelectedCategory(value);
            setCurrentPage(1); // Reset to first page on category change
          }}
          defaultValue="all"
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Select Category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.value} value={category.value}>
                {category.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Article List */}
      {paginatedArticles.length === 0 ? (
        <p>No articles available at the moment.</p>
      ) : (
        paginatedArticles.map((article) => (
          <div
            key={article.id}
            className="p-6 border rounded-md shadow-md bg-white"
          >
            <h3 className="text-xl font-semibold mb-2">
              <Link
                to={`/knowledge-base/${article.slug}`}
                className="text-primary hover:underline"
              >
                {article.title}
              </Link>
            </h3>
            <p className="text-sm text-gray-600 mb-4">{article.summary}</p>
            <Link
              to={`/knowledge-base/${article.slug}`}
              className="text-primary hover:underline text-xs"
            >
              Read More
            </Link>
          </div>
        ))
      )}

      {/* Pagination Controls */}
      {filteredArticles.length > ARTICLES_PER_PAGE && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="px-4 py-2 text-white disabled:opacity-50 text-sm bg-accent rounded p-2"
          >
            Previous
          </button>
          <span className="text-white text-sm rounded bg-gray-300 p-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-primary rounded text-gray-100 disabled:opacity-50 text-sm"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseContent;
